// Argus — useDragRectangle hook pentru band-select Windows Explorer style.
// Bloc file-explorer-multiselect-download 2026-05-06.
//
// Mouse rectangle band-select:
// - mousedown pe spațiu gol (ignore items + headers prin caller-supplied filter)
// - mousemove → update overlay rect live + recalculate intersection
// - mouseup → call onComplete cu Set<key> intersected + modifiers
// Mouseup global pe document (handle cazul mouse leave window).
//
// Limitare known: drag selectează DOAR items vizibile în viewport (Virtuoso recyclează DOM
// la scroll). Pattern matching Windows Explorer.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

export interface UseDragRectangleOptions<K> {
  /** Container ref — listener mousedown se atașează aici. */
  containerRef: RefObject<HTMLElement>;
  /** CSS selector pentru cell-uri eligibile pentru selecție (ex: '[data-fs-entry]'). */
  itemSelector: string;
  /** Citește cheia (entry name) dintr-un element care match itemSelector. */
  getKeyFromElement: (el: Element) => K | null;
  /**
   * CSS selector pentru zone unde drag NU pornește (mousedown ignorat).
   * Ex: '[data-fs-entry], [role=row]' (items + headers DataGrid).
   */
  ignoreSelector?: string;
  /**
   * Bloc file-explorer-multiselect-download iter2 2026-05-06: snapshot la mousedown
   * pentru CTRL/SHIFT+drag (păstrare existing). noModifier+drag = replace cu intersected.
   */
  getInitialSelection?: () => ReadonlySet<K>;
  /** Live update la fiecare mousemove — caller setează state direct cu noul Set. */
  onLiveUpdate?: (merged: ReadonlySet<K>, modifiers: { ctrlKey: boolean; shiftKey: boolean }) => void;
  /** Apelat la mouseup cu items intersectate + modifiers. Final commit. */
  onComplete: (merged: ReadonlySet<K>, modifiers: { ctrlKey: boolean; shiftKey: boolean }) => void;
  /**
   * Apelat când user face click simplu pe spațiu gol fără modifier (sub threshold drag).
   * Caller deselectează tot. Skip dacă undefined.
   */
  onEmptyClick?: () => void;
}

export interface UseDragRectangleResult {
  isDragging: boolean;
  /** Style pentru overlay <div> absolut pozițional. */
  rectStyle: CSSProperties;
}

interface DragState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

export function useDragRectangle<K>(opts: UseDragRectangleOptions<K>): UseDragRectangleResult {
  const { containerRef, itemSelector, getKeyFromElement, ignoreSelector, getInitialSelection, onLiveUpdate, onComplete, onEmptyClick } = opts;
  const [drag, setDrag] = useState<DragState | null>(null);
  // Track event flags pentru CTRL/SHIFT — modifiers la mousedown apply la onComplete.
  const modifiersRef = useRef<{ ctrlKey: boolean; shiftKey: boolean }>({ ctrlKey: false, shiftKey: false });
  // Snapshot selection la mousedown — pentru merge correct CTRL/SHIFT+drag fără double-counting.
  const initialSelectionRef = useRef<ReadonlySet<K>>(new Set());

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return; // doar left button.
      const container = containerRef.current;
      if (!container) return;

      // Verifică dacă target e în zona ignored (item sau header).
      const target = e.target as Element | null;
      if (target && ignoreSelector && target.closest(ignoreSelector)) return;

      // Coords relative la container — sau client coords (overlay folosește fixed/absolute pe container).
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      modifiersRef.current = { ctrlKey: e.ctrlKey, shiftKey: e.shiftKey };
      // Snapshot existing selection pentru merge la CTRL/SHIFT+drag.
      initialSelectionRef.current = getInitialSelection ? getInitialSelection() : new Set();
      setDrag({ startX: x, startY: y, currentX: x, currentY: y });
      e.preventDefault();
    },
    [containerRef, ignoreSelector],
  );

  // Atașează mousedown pe container.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('mousedown', handleMouseDown);
    return () => container.removeEventListener('mousedown', handleMouseDown);
  }, [containerRef, handleMouseDown]);

  // Mousemove + mouseup global pe document când drag activ.
  useEffect(() => {
    if (!drag) return;

    function computeIntersected(currentX: number, currentY: number): ReadonlySet<K> {
      const container = containerRef.current;
      if (!container || !drag) return new Set();
      const containerRect = container.getBoundingClientRect();
      const left = Math.min(drag.startX, currentX) + containerRect.left;
      const top = Math.min(drag.startY, currentY) + containerRect.top;
      const right = Math.max(drag.startX, currentX) + containerRect.left;
      const bottom = Math.max(drag.startY, currentY) + containerRect.top;
      const items = container.querySelectorAll<HTMLElement>(itemSelector);
      const intersected = new Set<K>();
      items.forEach((el) => {
        const elRect = el.getBoundingClientRect();
        const overlap =
          elRect.left < right &&
          elRect.right > left &&
          elRect.top < bottom &&
          elRect.bottom > top;
        if (overlap) {
          const key = getKeyFromElement(el);
          if (key !== null) intersected.add(key);
        }
      });
      return intersected;
    }

    function mergeWithModifiers(intersected: ReadonlySet<K>): ReadonlySet<K> {
      const mods = modifiersRef.current;
      if (mods.ctrlKey || mods.shiftKey) {
        // Additive merge cu snapshot inițial (NU cu state curent, evită double-counting).
        const merged = new Set<K>(initialSelectionRef.current);
        for (const k of intersected) merged.add(k);
        return merged;
      }
      return intersected;
    }

    const handleMove = (e: MouseEvent): void => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setDrag((prev) => (prev ? { ...prev, currentX: x, currentY: y } : null));
      // Live update — calc intersection cu poziția curentă + emit la caller.
      if (onLiveUpdate && drag) {
        const dx = x - drag.startX;
        const dy = y - drag.startY;
        if (Math.abs(dx) >= 4 || Math.abs(dy) >= 4) {
          const intersected = computeIntersected(x, y);
          const merged = mergeWithModifiers(intersected);
          onLiveUpdate(merged, modifiersRef.current);
        }
      }
    };

    const handleUp = (): void => {
      const container = containerRef.current;
      if (!container || !drag) {
        setDrag(null);
        return;
      }
      // Threshold 4px — click simplu sub threshold = "click pe spațiu gol".
      const dx = drag.currentX - drag.startX;
      const dy = drag.currentY - drag.startY;
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) {
        setDrag(null);
        // Pattern Windows Explorer: click simplu pe spațiu gol = clear selection.
        // Skip dacă CTRL/SHIFT (păstrăm existing), conform pattern.
        if (!modifiersRef.current.ctrlKey && !modifiersRef.current.shiftKey && onEmptyClick) {
          onEmptyClick();
        }
        return;
      }

      const intersected = computeIntersected(drag.currentX, drag.currentY);
      const merged = mergeWithModifiers(intersected);
      onComplete(merged, modifiersRef.current);
      setDrag(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [drag, containerRef, itemSelector, getKeyFromElement, onComplete]);

  const rectStyle: CSSProperties = drag
    ? {
        position: 'absolute',
        left: Math.min(drag.startX, drag.currentX),
        top: Math.min(drag.startY, drag.currentY),
        width: Math.abs(drag.currentX - drag.startX),
        height: Math.abs(drag.currentY - drag.startY),
        backgroundColor: 'rgba(25, 118, 210, 0.16)', // primary-tinted
        border: '1px solid rgba(25, 118, 210, 0.5)',
        pointerEvents: 'none',
        zIndex: 100,
      }
    : { display: 'none' };

  return { isDragging: drag !== null, rectStyle };
}
